import React, { useState, useEffect } from 'react';
import { Database, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';

const STORAGE_KEY = 'crm_sample_data_enabled';

export default function SampleDataToggle({ 
  entityType, 
  onToggle, 
  isActive = false,
  className = '' 
}) {
  const { userRoles, isSuperAdmin, profile } = useAuth();
  
  // Read initial state from localStorage, scoped to tenant
  const getStoredState = () => {
    try {
      const stored = localStorage?.getItem(`${STORAGE_KEY}_${profile?.tenant_id}`);
      return stored === 'true';
    } catch {
      return false;
    }
  };

  const [showSampleData, setShowSampleData] = useState(getStoredState);
  const [loading, setLoading] = useState(false);

  // Sync with parent component's isActive prop on mount
  useEffect(() => {
    const storedState = getStoredState();
    if (storedState !== isActive) {
      // Notify parent of stored state
      onToggle?.(storedState);
    }
  }, []);

  // Check if user has permission to view sample data
  const hasPermission = () => {
    if (isSuperAdmin()) return true;
    
    const allowedRoles = ['business_administrator', 'manager', 'staff'];
    return userRoles?.some(role => 
      allowedRoles?.includes(role?.name?.toLowerCase()?.replace(/\s+/g, '_'))
    );
  };

  // Don't render if user doesn't have permission
  if (!hasPermission()) {
    return null;
  }

  const handleToggle = async () => {
    setLoading(true);
    try {
      const newState = !showSampleData;
      setShowSampleData(newState);
      
      // Persist to localStorage (scoped to tenant)
      try {
        localStorage?.setItem(`${STORAGE_KEY}_${profile?.tenant_id}`, String(newState));
      } catch (err) {
        console.error('Failed to persist sample data state:', err);
      }
      
      await onToggle?.(newState);
    } catch (error) {
      console.error('Error toggling sample data:', error);
      // Revert on error
      setShowSampleData(!showSampleData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        variant={showSampleData ? 'primary' : 'outline'}
        size="sm"
        onClick={handleToggle}
        disabled={loading}
        className="flex items-center gap-2"
      >
        {loading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <Database className="h-4 w-4" />
        )}
        {showSampleData ? 'Hide Sample Data' : 'Show Sample Data'}
      </Button>
      {showSampleData && (
        <span className="text-xs text-gray-500 italic">
          Viewing sample {entityType}
        </span>
      )}
    </div>
  );
}